import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Shield } from 'lucide-react';

interface AntiLossStreakControlsProps {
  enabled: boolean;
  waitForLosses: number;
  stopAfterLosses: number;
  resetOnWin: boolean;
  mode: 'consecutive_losses' | 'consecutive_trades';
  onEnabledChange: (enabled: boolean) => void;
  onWaitForLossesChange: (value: number) => void;
  onStopAfterLossesChange: (value: number) => void;
  onResetOnWinChange: (enabled: boolean) => void;
  onModeChange: (mode: 'consecutive_losses' | 'consecutive_trades') => void;
}

export const AntiLossStreakControls: React.FC<AntiLossStreakControlsProps> = ({
  enabled,
  waitForLosses,
  stopAfterLosses,
  resetOnWin,
  mode,
  onEnabledChange,
  onWaitForLossesChange,
  onStopAfterLossesChange,
  onResetOnWinChange,
  onModeChange,
}) => {
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Shield className="h-5 w-5" />
          Anti Loss Streak
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="anti-loss-streak-enabled"
            checked={enabled}
            onCheckedChange={onEnabledChange}
          />
          <Label htmlFor="anti-loss-streak-enabled">
            Enable Anti Loss Streak Protection
          </Label>
        </div>

        {enabled && (
          <div className="space-y-4 pl-6 border-l-2 border-muted">
            <div className="space-y-3">
              <Label className="text-sm font-medium">Stop Mode</Label>
              <RadioGroup value={mode} onValueChange={onModeChange}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="consecutive_losses" id="anti-mode-losses" />
                  <Label htmlFor="anti-mode-losses" className="text-sm">
                    Stop after M consecutive losses
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="consecutive_trades" id="anti-mode-trades" />
                  <Label htmlFor="anti-mode-trades" className="text-sm">
                    Stop after M trades (any outcome)
                  </Label>
                </div>
              </RadioGroup>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="wait-for-losses">
                  Wait for {waitForLosses} consecutive losses before entering
                </Label>
                <Input
                  id="wait-for-losses"
                  type="number"
                  min="1"
                  max="20"
                  value={waitForLosses}
                  onChange={(e) => onWaitForLossesChange(parseInt(e.target.value) || 1)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="stop-after-losses">
                  Stop after {stopAfterLosses} {mode === 'consecutive_losses' ? 'consecutive losses' : 'trades'}
                </Label>
                <Input
                  id="stop-after-losses"
                  type="number"
                  min="1"
                  max="20"
                  value={stopAfterLosses}
                  onChange={(e) => onStopAfterLossesChange(parseInt(e.target.value) || 1)}
                />
                <p className="text-xs text-muted-foreground">
                  {mode === 'consecutive_losses' 
                    ? 'Stop after this many consecutive losses' 
                    : 'Stop after this many trades (wins or losses)'}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="reset-on-win"
                checked={resetOnWin}
                onCheckedChange={onResetOnWinChange}
              />
              <Label htmlFor="reset-on-win">
                Reset after win while paused
              </Label>
            </div>

            <div className="mt-4 p-3 bg-muted/50 rounded-lg">
              <h4 className="font-medium text-sm mb-2">How it works:</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• <strong>WAITING:</strong> Skip trades until {waitForLosses} consecutive losses occur</li>
                <li>• <strong>ACTIVE:</strong> Take trades; {mode === 'consecutive_losses' ? `the ${stopAfterLosses}th loss is still included, then pause from the next trade` : `stop after ${stopAfterLosses} trades (any outcome)`}</li>
                <li>• <strong>PAUSED:</strong> Skip all trades until a win; that win is skipped and resets to WAITING</li>
                <li>• <strong>RESET:</strong> Triggered only by a win while paused</li>
              </ul>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};